import { action } from "typesafe-actions";
import {
  IAutomobileCancellationReinstatement,
  IAutomobileInsurance,
  IAutomobileOPCF47R,
  IAutomobileOPCF48,
  IAutomobileOPCF49,
  IAutomobileSEF13D,
  IAutomobileSEF13H,
  IAutomobileSEF16,
  IAutomobileSEF17,
  IAutomobileSEF19,
  IAutomobileSEF40,
  IIntactAuthorization,
  IPolicyDriverAbstracts,
  IPolicyLiabilitySlips,
  IPolicyTelematics,
  IPolicyUpdates,
  IPremiumFinance,
} from "./policyCommon.types";
import { ICustomerContact } from "../customer/customer.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { api } from "../../api/api";
import {
  uploadFile,
  uploadMultipleFile,
} from "../../features/FileUpload/utils";
import { ERROR_MESSAGES } from "../../constants/enums";
import { makeApiCall } from "../../helpers/postRequest";
import { IFileUpload } from "../../features/FileUpload/components/FileUpload.type";
import { getUniqueId } from "../../helpers";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { endPoints } from "../../constants/endPoints";

export const FETCH_POLICY_COMMON_TELEMATICS_LIST_PROGRESS =
  "FETCH_POLICY_COMMON_TELEMATICS_LIST_PROGRESS";
export const FETCH_POLICY_COMMON_TELEMATICS_LIST_SUCCESS =
  "FETCH_POLICY_COMMON_TELEMATICS_LIST_SUCCESS";
export const FETCH_POLICY_COMMON_TELEMATICS_LIST_FAILED =
  "FETCH_POLICY_COMMON_TELEMATICS_LIST_FAILED";

export const fetchPolicyCommonTelematicsListProgress = () =>
  action(FETCH_POLICY_COMMON_TELEMATICS_LIST_PROGRESS);
export const fetchPolicyCommonTelematicsListSuccess = (
  list: IPolicyTelematics[],
  totalRecords: number
) =>
  action(FETCH_POLICY_COMMON_TELEMATICS_LIST_SUCCESS, { list, totalRecords });
export const fetchPolicyCommonTelematicsListFailed = () =>
  action(FETCH_POLICY_COMMON_TELEMATICS_LIST_FAILED);

export const FETCH_POLICY_COMMON_CONTACTS_LIST_PROGRESS =
  "FETCH_POLICY_COMMON_CONTACTS_LIST_PROGRESS";
export const FETCH_POLICY_COMMON_CONTACTS_LIST_SUCCESS =
  "FETCH_POLICY_COMMON_CONTACTS_LIST_SUCCESS";
export const FETCH_POLICY_COMMON_CONTACTS_LIST_FAILED =
  "FETCH_POLICY_COMMON_CONTACTS_LIST_FAILED";

export const fetchPolicyCommonContactsListProgress = () =>
  action(FETCH_POLICY_COMMON_CONTACTS_LIST_PROGRESS);
export const fetchPolicyCommonContactsListSuccess = (
  data: ICustomerContact[]
) => action(FETCH_POLICY_COMMON_CONTACTS_LIST_SUCCESS, { data });
export const fetchPolicyCommonContactsListFailed = () =>
  action(FETCH_POLICY_COMMON_CONTACTS_LIST_FAILED);

export const fetchPolicyCommonContactsListAsync =
  (
    customer_id: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPolicyCommonContactsListProgress());
      const res = await api.get(
        `/customer/get-secondary-contact?customer_id=${customer_id}`
      );
      const data: ICustomerContact[] = res.data.data;
      dispatch(fetchPolicyCommonContactsListSuccess(data));
    } catch (err: any) {
      dispatch(fetchPolicyCommonContactsListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertPolicyCommonContactAsync =
  (
    data: ICustomerContact,
    customer_policy_id: number,
    onCallback: (isSuccess?: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      await api.post("/customer/create-secondary-contact", {
        ...data,
        customer_policy_id,
        status: "ACTIVE",
      });

      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );

      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchPolicyCommonTelematicsListAsyc =
  (
    customer_policy_id: number,
    status: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPolicyCommonTelematicsListProgress());
      let finalUrl = `/policy/get-telematics-form?customer_policy_id=${customer_policy_id}`;
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }

      const res = await api.get(finalUrl);
      const data: IPolicyTelematics[] = res.data.data;

      dispatch(
        fetchPolicyCommonTelematicsListSuccess(data, res.data.totalRecords)
      );
    } catch (err: any) {
      dispatch(fetchPolicyCommonTelematicsListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertPolicyCommonTelematicsAsync =
  (
    data: IPolicyTelematics,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      await api.post("/policy/edit-telematics-form", data);

      dispatch(
        showMessage({
          type: "success",
          message: "Policy Commission saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const downloadPolicyTelematicsPDFAsync =
  (
    incremental_number: string,
    customer_policy_id: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/policy/get-telematics-form-pdf?incremental_number=${incremental_number}&customer_policy_id=${customer_policy_id}`,
        {
          responseType: "arraybuffer",
        }
      );

      const pdfBuffer = res.data;

      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "downloaded.pdf";
      link.click();

      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const previewPolicyTelematicsPDFAsync =
  (
    incremental_number: string,
    customer_policy_id: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/policy/get-telematics-form-pdf?incremental_number=${incremental_number}&is_telematics_preview=true&&customer_policy_id=${customer_policy_id}`
      );

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      onCallback();
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_POLICY_ARS_EXTRACT_LIST_PROGRESS =
  "FETCH_POLICY_ARS_EXTRACT_LIST_PROGRESS";
export const FETCH_POLICY_ARS_EXTRACT_LIST_SUCCESS =
  "FETCH_POLICY_ARS_EXTRACT_LIST_SUCCESS";
export const FETCH_POLICY_ARS_EXTRACT_LIST_FAILED =
  "FETCH_POLICY_ARS_EXTRACT_LIST_FAILED";

export const fetchPolicyArsExtractListProgress = () =>
  action(FETCH_POLICY_ARS_EXTRACT_LIST_PROGRESS);

export const fetchPolicyArsExtractListSuccess = (
  data: IPolicyDriverAbstracts[]
) =>
  action(FETCH_POLICY_ARS_EXTRACT_LIST_SUCCESS, {
    data,
  });
export const fetchPolicyArsExtractListFailed = () =>
  action(FETCH_POLICY_ARS_EXTRACT_LIST_FAILED);

export const fetchPolicyArsExtractListAsync =
  (
    customerPolicyId: number,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPolicyArsExtractListProgress());

      let finalUrl = `/policy/get-policy-mvr-abstracts?customer_policy_id=${customerPolicyId}`;

      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl);
      const data: IPolicyDriverAbstracts[] = res.data.data;
      console.log("data", data);
      dispatch(fetchPolicyArsExtractListSuccess(data));
    } catch (err: any) {
      dispatch(fetchPolicyArsExtractListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertARSExtract =
  (
    file: File,
    customer_policy_id: number,
    onCallback: (isSuccess: boolean, code?: string, isAlready?: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        page_name: "test",
      };

      const filePath = await uploadFile(file, "TEST", "", asPayload);

      const res = await api.post("/general/extract-mvr-abstracts-with-gen-ai", {
        url: filePath,
        customer_policy_id: customer_policy_id,
      });
      const proccessCode = res.data.data.process_records_code;
      if (!proccessCode) {
        dispatch(
          showMessage({
            type: "success",
            message: "Application data already extracted!",
            displayAs: "snackbar",
          })
        );
        onCallback(true, undefined, true);
      }
      onCallback(true, proccessCode);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
  
// OPCF 47R
// export const upsertOpcf47R = createAsyncThunk<
//   IAutomobileOPCF47R,
//   {
//     opcf47r: IAutomobileOPCF47R;
//     initialData: IAutomobileOPCF47R;
//     isUpdate: boolean;
//     policyType: string;
//     isDuplicate?: boolean;
//   },
//   { rejectValue: APIError }
// >(
//   "policyCommon/upsertOpcf47R",
//   async (payload, { rejectWithValue }) => {
//     const { opcf47r, initialData, isUpdate, policyType, isDuplicate } = payload;

//     try {
//       const res = await makeApiCall(
//         {
//           url: "/policy/edit-policy-opcf-forty-seven-r-certificate",
//           method: "POST",
//           automation: {
//             isUpdate,
//             primaryFieldName: "customer_policy_id",
//           },
//         },
//         initialData,
//         {
//           ...opcf47r,
//           policy_type: policyType,
//           ...(isDuplicate && { incremental_number: null }),
//         }
//       );

//       return res.data.data as IAutomobileOPCF47R;
//     } catch (err: any) {
//       return rejectWithValue({
//         message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
//         status: err.response?.status,
//       });
//     }
//   }
// );

export const upsertOpcf47RAsync =
  (
    opcf47r: IAutomobileOPCF47R,
    initialData: IAutomobileOPCF47R,
    isUpdate: boolean,
    policyType: string,
    onCallback: (isSuccess: boolean) => void,
    isDuplicate?: boolean,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await makeApiCall(
        {
          url: "/policy/edit-policy-opcf-forty-seven-r-certificate",
          method: "POST",
          automation: {
            isUpdate,
            primaryFieldName: "customer_policy_id",
          },
        },
        initialData,
        {
          ...opcf47r,
          policy_type: policyType,
          ...(isDuplicate && { incremental_number: null }),
        },
      );
      onCallback(true);
      dispatch(
        showMessage({
          type: "success",
          message: "OPCF47R saved successfully!",
          displayAs: "snackbar",
        })
      );
    } catch (err: any) {
      dispatch(fetchPolicyOpCf47RFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };


export const FETCH_POLICY_OPCF47R_PROGRESS = "FETCH_POLICY_OPCF47R_PROGRESS";
export const FETCH_POLICY_OPCF47R_SUCCESS = "FETCH_POLICY_OPCF47R_SUCCESS";
export const FETCH_POLICY_OPCF47R_FAILED = "FETCH_POLICY_OPCF47R_FAILED";

export const fetchPolicyOpCf47RProgress = () =>
  action(FETCH_POLICY_OPCF47R_PROGRESS);

export const fetchPolicyOpCf47RSuccess = (data: IAutomobileOPCF47R) =>
  action(FETCH_POLICY_OPCF47R_SUCCESS, {
    data,
  });
export const fetchPolicyOpCf47RFailed = (errorMessage: string) =>
  action(FETCH_POLICY_OPCF47R_FAILED, { errorMessage });

export const fetchOpcf47RAsync = 
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPolicyOpCf47RProgress());
      const res = await api.get(
        `/policy/get-policy-opcf-forty-seven-r-certificate?customer_policy_id=${customerPolicyId}&column=incremental_number&value=${incremental_number}&policy_type=${policyType}`
      );
      const data: IAutomobileOPCF47R[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchPolicyOpCf47RSuccess({ ...data[0] }));
      } else {
        dispatch(fetchPolicyOpCf47RFailed(ERROR_MESSAGES.POLICY_NOT_FOUND));
      }
    } catch (err: any) {
      dispatch(fetchPolicyOpCf47RFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const downloadOpcf47RPDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-policy-opcf-forty-seven-r-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}`,
        {
          responseType: "arraybuffer",
        }
      );
      const pdfBuffer = res.data;
      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "downloaded.pdf";
      link.click();
      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicyOpCf47RFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const previewOpcf47RPDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-policy-opcf-forty-seven-r-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}&is_opcf_forty_seven_r_certificate_preview=true`
      );
      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicyOpCf47RFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

//OPCF 48

export const FETCH_POLICY_OPCF48_PROGRESS = "FETCH_POLICY_OPCF48_PROGRESS";
export const FETCH_POLICY_OPCF48_SUCCESS = "FETCH_POLICY_OPCF48_SUCCESS";
export const FETCH_POLICY_OPCF48_FAILED = "FETCH_POLICY_OPCF48_FAILED";

export const fetchPolicyOpCf48Progress = () =>
  action(FETCH_POLICY_OPCF48_PROGRESS);

export const fetchPolicyOpCf48Success = (data: IAutomobileOPCF48) =>
  action(FETCH_POLICY_OPCF48_SUCCESS, {
    data,
  });
export const fetchPolicyOpCf48Failed = (errorMessage: string) =>
  action(FETCH_POLICY_OPCF48_FAILED, { errorMessage });

export const addOpcf48sync =
  (
    data: IAutomobileOPCF48,
    initialData: IAutomobileOPCF48,
    isUpdate: boolean,
    policyType: string,
    onCallback: (isSuccess: boolean) => void,
    isDuplicate?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      if (isDuplicate) {
        await makeApiCall(
          {
            url: "/policy/edit-policy-opcf-forty-eight-certificate",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            policy_type: policyType,
            incremental_number: null,
          }
        );
        // await api.post("/policy/edit-policy-opcf-forty-eight-certificate", {
        //   ...data,
        //   policy_type: policyType,
        //   incremental_number: null,
        // });
      } else {
        await makeApiCall(
          {
            url: "/policy/edit-policy-opcf-forty-eight-certificate",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            policy_type: policyType,
          }
        );
        // await api.post("/policy/edit-policy-opcf-forty-eight-certificate", {
        //   ...data,
        //   policy_type: policyType,
        // });
      }

      onCallback(true);
      dispatch(
        showMessage({
          type: "success",
          message: "OPCF48 saved successfully!",
          displayAs: "snackbar",
        })
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchOpcf48Async =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPolicyOpCf48Progress());
      const res = await api.get(
        `/policy/get-policy-opcf-forty-eight-certificate?customer_policy_id=${customerPolicyId}&column=incremental_number&value=${incremental_number}&policy_type=${policyType}`
      );
      const data: IAutomobileOPCF48[] = res.data.data;
      if (data.length > 0) {
        dispatch(
          fetchPolicyOpCf48Success({
            ...data[0],
          })
        );
      } else {
        dispatch(fetchPolicyOpCf48Failed(ERROR_MESSAGES.POLICY_NOT_FOUND));
      }
    } catch (err: any) {
      dispatch(fetchPolicyOpCf48Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const downloadOpcf48PDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-policy-opcf-forty-eight-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}`,
        {
          responseType: "arraybuffer",
        }
      );

      const pdfBuffer = res.data;

      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "downloaded.pdf";
      link.click();

      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicyOpCf48Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const previewOpcf48PDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-policy-opcf-forty-eight-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}&is_opcf_forty_eight_certificate_preview=true`
      );

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicyOpCf48Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// OPCF 49
export const FETCH_POLICY_OPCF49_PROGRESS = "FETCH_POLICY_OPCF49_PROGRESS";
export const FETCH_POLICY_OPCF49_SUCCESS = "FETCH_POLICY_OPCF49_SUCCESS";
export const FETCH_POLICY_OPCF49_FAILED = "FETCH_POLICY_OPCF49_FAILED";

export const fetchPolicyOpCf49Progress = () =>
  action(FETCH_POLICY_OPCF49_PROGRESS);

export const fetchPolicyOpCf49Success = (data: IAutomobileOPCF49) =>
  action(FETCH_POLICY_OPCF49_SUCCESS, {
    data,
  });
export const fetchPolicyOpCf49Failed = (errorMessage: string) =>
  action(FETCH_POLICY_OPCF49_FAILED, { errorMessage });

export const addOpcf49sync =
  (
    data: IAutomobileOPCF49,
    initialData: IAutomobileOPCF49,
    isUpdate: boolean,
    policyType: string,
    onCallback: (isSuccess: boolean) => void,
    isDuplicate?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      if (isDuplicate) {
        await makeApiCall(
          {
            url: "/policy/edit-policy-opcf-forty-nine-certificate",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            policy_type: policyType,
            incremental_number: null,
          }
        );
        // await api.post("/policy/edit-policy-opcf-forty-nine-certificate", {
        //   ...data,
        //   policy_type: policyType,
        //   incremental_number: null,
        // });
      } else {
        await makeApiCall(
          {
            url: "/policy/edit-policy-opcf-forty-nine-certificate",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            policy_type: policyType,
          }
        );
        // await api.post("/policy/edit-policy-opcf-forty-nine-certificate", {
        //   ...data,
        //   policy_type: policyType,
        // });
      }

      onCallback(true);
      dispatch(
        showMessage({
          type: "success",
          message: "OPCF49 saved successfully!",
          displayAs: "snackbar",
        })
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchOpcf49Async =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPolicyOpCf49Progress());
      const res = await api.get(
        `/policy/get-policy-opcf-forty-nine-certificate?customer_policy_id=${customerPolicyId}&column=incremental_number&value=${incremental_number}&policy_type=${policyType}`
      );
      const data: IAutomobileOPCF49[] = res.data.data;
      if (data.length > 0) {
        dispatch(
          fetchPolicyOpCf49Success({
            ...data[0],
          })
        );
      } else {
        dispatch(fetchPolicyOpCf49Failed(ERROR_MESSAGES.POLICY_NOT_FOUND));
      }
    } catch (err: any) {
      dispatch(fetchPolicyOpCf49Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const downloadOpcf49PDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-policy-opcf-forty-nine-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}`,
        {
          responseType: "arraybuffer",
        }
      );

      const pdfBuffer = res.data;

      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "downloaded.pdf";
      link.click();

      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicyOpCf49Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const previewOpcf49PDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-policy-opcf-forty-nine-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}&is_opcf_forty_nine_certificate_preview=true`
      );

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicyOpCf49Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// SEF 16
export const FETCH_POLICY_SEF16_PROGRESS = "FETCH_POLICY_SEF16_PROGRESS";
export const FETCH_POLICY_SEF16_SUCCESS = "FETCH_POLICY_SEF16_SUCCESS";
export const FETCH_POLICY_SEF16_FAILED = "FETCH_POLICY_SEF16_FAILED";

export const fetchPolicySef16Progress = () =>
  action(FETCH_POLICY_SEF16_PROGRESS);

export const fetchPolicySef16Success = (data: IAutomobileSEF16) =>
  action(FETCH_POLICY_SEF16_SUCCESS, {
    data,
  });
export const fetchPolicySef16Failed = (errorMessage: string) =>
  action(FETCH_POLICY_SEF16_FAILED, { errorMessage });

export const addSef16Async =
  (
    data: IAutomobileSEF16,
    initialData: IAutomobileSEF16,
    isUpdate: boolean,
    policyType: string,
    onCallback: (isSuccess: boolean) => void,
    isDuplicate?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      if (isDuplicate) {
        await makeApiCall(
          {
            url: "/policy/edit-policy-alberta-sef-sixteen-certificate",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            policy_type: policyType,
            incremental_number: null,
          }
        );
      } else {
        await makeApiCall(
          {
            url: "/policy/edit-policy-alberta-sef-sixteen-certificate",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            policy_type: policyType,
          }
        );
      }

      onCallback(true);
      dispatch(
        showMessage({
          type: "success",
          message: "SEF16 saved successfully!",
          displayAs: "snackbar",
        })
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchSef16Async =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPolicySef16Progress());
      const res = await api.get(
        `/policy/get-alberta-sef-sixteen-certificate?customer_policy_id=${customerPolicyId}&column=incremental_number&value=${incremental_number}&policy_type=${policyType}`
      );
      const data: IAutomobileSEF16[] = res.data.data;
      if (data.length > 0) {
        dispatch(
          fetchPolicySef16Success({
            ...data[0],
          })
        );
      } else {
        dispatch(fetchPolicySef16Failed(ERROR_MESSAGES.POLICY_NOT_FOUND));
      }
    } catch (err: any) {
      dispatch(fetchPolicySef16Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const downloadSef16PDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-alberta-sef-sixteen-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}`,
        {
          responseType: "arraybuffer",
        }
      );

      const pdfBuffer = res.data;

      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "downloaded.pdf";
      link.click();

      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicySef16Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const previewSef16PDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-alberta-sef-sixteen-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}&is_sef_sixteen_certificate_preview=true`
      );

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicySef16Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// SEF 17
export const FETCH_POLICY_SEF17_PROGRESS = "FETCH_POLICY_SEF17_PROGRESS";
export const FETCH_POLICY_SEF17_SUCCESS = "FETCH_POLICY_SEF17_SUCCESS";
export const FETCH_POLICY_SEF17_FAILED = "FETCH_POLICY_SEF17_FAILED";

export const fetchPolicySef17Progress = () =>
  action(FETCH_POLICY_SEF17_PROGRESS);

export const fetchPolicySef17Success = (data: IAutomobileSEF17) =>
  action(FETCH_POLICY_SEF17_SUCCESS, {
    data,
  });
export const fetchPolicySef17Failed = (errorMessage: string) =>
  action(FETCH_POLICY_SEF17_FAILED, { errorMessage });

export const addSef17Async =
  (
    data: IAutomobileSEF17,
    initialData: IAutomobileSEF17,
    isUpdate: boolean,
    policyType: string,
    onCallback: (isSuccess: boolean) => void,
    isDuplicate?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      if (isDuplicate) {
        await makeApiCall(
          {
            url: "/policy/edit-policy-alberta-sef-seventeen-certificate",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            policy_type: policyType,
            incremental_number: null,
          }
        );
      } else {
        await makeApiCall(
          {
            url: "/policy/edit-policy-alberta-sef-seventeen-certificate",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            policy_type: policyType,
          }
        );
      }

      onCallback(true);
      dispatch(
        showMessage({
          type: "success",
          message: "SEF17 saved successfully!",
          displayAs: "snackbar",
        })
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchSef17Async =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPolicySef17Progress());
      const res = await api.get(
        `/policy/get-alberta-sef-seventeen-certificate?customer_policy_id=${customerPolicyId}&column=incremental_number&value=${incremental_number}&policy_type=${policyType}`
      );
      const data: IAutomobileSEF17[] = res.data.data;
      if (data.length > 0) {
        dispatch(
          fetchPolicySef17Success({
            ...data[0],
          })
        );
      } else {
        dispatch(fetchPolicySef17Failed(ERROR_MESSAGES.POLICY_NOT_FOUND));
      }
    } catch (err: any) {
      dispatch(fetchPolicySef17Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const downloadSef17PDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-alberta-sef-seventeen-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}`,
        {
          responseType: "arraybuffer",
        }
      );

      const pdfBuffer = res.data;

      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "downloaded.pdf";
      link.click();

      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicySef17Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const previewSef17PDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-alberta-sef-seventeen-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}&is_sef_seventeen_certificate_preview=true`
      );

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicySef17Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };
// SEF 19
export const FETCH_POLICY_SEF19_PROGRESS = "FETCH_POLICY_SEF19_PROGRESS";
export const FETCH_POLICY_SEF19_SUCCESS = "FETCH_POLICY_SEF19_SUCCESS";
export const FETCH_POLICY_SEF19_FAILED = "FETCH_POLICY_SEF19_FAILED";

export const fetchPolicySef19Progress = () =>
  action(FETCH_POLICY_SEF19_PROGRESS);

export const fetchPolicySef19Success = (data: IAutomobileSEF19) =>
  action(FETCH_POLICY_SEF19_SUCCESS, {
    data,
  });
export const fetchPolicySef19Failed = (errorMessage: string) =>
  action(FETCH_POLICY_SEF19_FAILED, { errorMessage });

export const addSef19Async =
  (
    data: IAutomobileSEF19,
    initialData: IAutomobileSEF19,
    isUpdate: boolean,
    policyType: string,
    onCallback: (isSuccess: boolean) => void,
    isDuplicate?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      if (isDuplicate) {
        await makeApiCall(
          {
            url: "/policy/edit-policy-alberta-sef-nineteen-certificate",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            policy_type: policyType,
            incremental_number: null,
          }
        );
      } else {
        await makeApiCall(
          {
            url: "/policy/edit-policy-alberta-sef-nineteen-certificate",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            policy_type: policyType,
          }
        );
      }

      onCallback(true);
      dispatch(
        showMessage({
          type: "success",
          message: "SEF19 saved successfully!",
          displayAs: "snackbar",
        })
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchSef19Async =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPolicySef19Progress());
      const res = await api.get(
        `/policy/get-alberta-sef-nineteen-certificate?customer_policy_id=${customerPolicyId}&column=incremental_number&value=${incremental_number}&policy_type=${policyType}`
      );
      const data: IAutomobileSEF19[] = res.data.data;
      if (data.length > 0) {
        dispatch(
          fetchPolicySef19Success({
            ...data[0],
          })
        );
      } else {
        dispatch(fetchPolicySef19Failed(ERROR_MESSAGES.POLICY_NOT_FOUND));
      }
    } catch (err: any) {
      dispatch(fetchPolicySef19Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const downloadSef19PDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-alberta-sef-nineteen-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}`,
        {
          responseType: "arraybuffer",
        }
      );

      const pdfBuffer = res.data;

      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "downloaded.pdf";
      link.click();

      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicySef19Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const previewSef19PDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-alberta-sef-nineteen-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}&is_sef_nineteen_certificate_preview=true`
      );

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicySef19Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };
// SEF 13D
export const FETCH_POLICY_SEF13D_PROGRESS = "FETCH_POLICY_SEF13D_PROGRESS";
export const FETCH_POLICY_SEF13D_SUCCESS = "FETCH_POLICY_SEF13D_SUCCESS";
export const FETCH_POLICY_SEF13D_FAILED = "FETCH_POLICY_SEF13D_FAILED";

export const fetchPolicySef13DProgress = () =>
  action(FETCH_POLICY_SEF13D_PROGRESS);

export const fetchPolicySef13DSuccess = (data: IAutomobileSEF13D) =>
  action(FETCH_POLICY_SEF13D_SUCCESS, {
    data,
  });
export const fetchPolicySef13DFailed = (errorMessage: string) =>
  action(FETCH_POLICY_SEF13D_FAILED, { errorMessage });

export const addSef13DAsync =
  (
    data: IAutomobileSEF13D,
    initialData: IAutomobileSEF13D,
    isUpdate: boolean,
    policyType: string,
    onCallback: (isSuccess: boolean) => void,
    isDuplicate?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      if (isDuplicate) {
        await makeApiCall(
          {
            url: "/policy/edit-policy-alberta-sef-thirteen-d-certificate",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            policy_type: policyType,
            incremental_number: null,
          }
        );
      } else {
        await makeApiCall(
          {
            url: "/policy/edit-policy-alberta-sef-thirteen-d-certificate",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            policy_type: policyType,
          }
        );
      }

      onCallback(true);
      dispatch(
        showMessage({
          type: "success",
          message: "SEF19 saved successfully!",
          displayAs: "snackbar",
        })
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchSef13DAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPolicySef13DProgress());
      const res = await api.get(
        `/policy/get-alberta-sef-thirteen-d-certificate?customer_policy_id=${customerPolicyId}&column=incremental_number&value=${incremental_number}&policy_type=${policyType}`
      );
      const data: IAutomobileSEF13D[] = res.data.data;
      if (data.length > 0) {
        dispatch(
          fetchPolicySef13DSuccess({
            ...data[0],
          })
        );
      } else {
        dispatch(fetchPolicySef13DFailed(ERROR_MESSAGES.POLICY_NOT_FOUND));
      }
    } catch (err: any) {
      dispatch(fetchPolicySef13DFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const downloadSef13DPDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-alberta-sef-thirteen-d-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}`,
        {
          responseType: "arraybuffer",
        }
      );

      const pdfBuffer = res.data;

      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "downloaded.pdf";
      link.click();

      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicySef13DFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const previewSef13DPDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-alberta-sef-thirteen-d-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}&is_sef_thirteen_d_certificate_preview=true`
      );

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicySef13DFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// SEF 13H
export const FETCH_POLICY_SEF13H_PROGRESS = "FETCH_POLICY_SEF13H_PROGRESS";
export const FETCH_POLICY_SEF13H_SUCCESS = "FETCH_POLICY_SEF13H_SUCCESS";
export const FETCH_POLICY_SEF13H_FAILED = "FETCH_POLICY_SEF13H_FAILED";

export const fetchPolicySef13HProgress = () =>
  action(FETCH_POLICY_SEF13H_PROGRESS);

export const fetchPolicySef13HSuccess = (data: IAutomobileSEF13H) =>
  action(FETCH_POLICY_SEF13H_SUCCESS, {
    data,
  });
export const fetchPolicySef13HFailed = (errorMessage: string) =>
  action(FETCH_POLICY_SEF13H_FAILED, { errorMessage });

export const addSef13HAsync =
  (
    data: IAutomobileSEF13H,
    initialData: IAutomobileSEF13H,
    isUpdate: boolean,
    policyType: string,
    onCallback: (isSuccess: boolean) => void,
    isDuplicate?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      if (isDuplicate) {
        await makeApiCall(
          {
            url: "/policy/edit-policy-alberta-sef-thirteen-h-certificate",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            policy_type: policyType,
            incremental_number: null,
          }
        );
      } else {
        await makeApiCall(
          {
            url: "/policy/edit-policy-alberta-sef-thirteen-h-certificate",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            policy_type: policyType,
          }
        );
      }

      onCallback(true);
      dispatch(
        showMessage({
          type: "success",
          message: "SEF19 saved successfully!",
          displayAs: "snackbar",
        })
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchSef13HAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPolicySef13HProgress());
      const res = await api.get(
        `/policy/get-alberta-sef-thirteen-h-certificate?customer_policy_id=${customerPolicyId}&column=incremental_number&value=${incremental_number}&policy_type=${policyType}`
      );
      const data: IAutomobileSEF13H[] = res.data.data;
      if (data.length > 0) {
        dispatch(
          fetchPolicySef13HSuccess({
            ...data[0],
          })
        );
      } else {
        dispatch(fetchPolicySef13HFailed(ERROR_MESSAGES.POLICY_NOT_FOUND));
      }
    } catch (err: any) {
      dispatch(fetchPolicySef13HFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const downloadSef13HPDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-alberta-sef-thirteen-h-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}`,
        {
          responseType: "arraybuffer",
        }
      );

      const pdfBuffer = res.data;

      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "downloaded.pdf";
      link.click();

      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicyOpCf49Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const previewSef13HPDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-alberta-sef-thirteen-h-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}&is_sef_thirteen_h_certificate_preview=true`
      );

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicySef13HFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };
// SEF 40
export const FETCH_POLICY_SEF40_PROGRESS = "FETCH_POLICY_SEF40_PROGRESS";
export const FETCH_POLICY_SEF40_SUCCESS = "FETCH_POLICY_SEF40_SUCCESS";
export const FETCH_POLICY_SEF40_FAILED = "FETCH_POLICY_SEF40_FAILED";

export const fetchPolicySef40Progress = () =>
  action(FETCH_POLICY_SEF40_PROGRESS);

export const fetchPolicySef40Success = (data: IAutomobileSEF40) =>
  action(FETCH_POLICY_SEF40_SUCCESS, {
    data,
  });
export const fetchPolicySef40Failed = (errorMessage: string) =>
  action(FETCH_POLICY_SEF40_FAILED, { errorMessage });

export const addSef40Async =
  (
    data: IAutomobileSEF40,
    initialData: IAutomobileSEF40,
    isUpdate: boolean,
    policyType: string,
    onCallback: (isSuccess: boolean) => void,
    isDuplicate?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      if (isDuplicate) {
        await makeApiCall(
          {
            url: "/policy/edit-policy-alberta-sef-forty-certificate",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            policy_type: policyType,
            incremental_number: null,
          }
        );
      } else {
        await makeApiCall(
          {
            url: "/policy/edit-policy-alberta-sef-forty-certificate",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            policy_type: policyType,
          }
        );
      }

      onCallback(true);
      dispatch(
        showMessage({
          type: "success",
          message: "SEF40 saved successfully!",
          displayAs: "snackbar",
        })
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchSef40Async =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPolicySef40Progress());
      const res = await api.get(
        `/policy/get-alberta-sef-forty-certificate?customer_policy_id=${customerPolicyId}&column=incremental_number&value=${incremental_number}&policy_type=${policyType}`
      );
      const data: IAutomobileSEF40[] = res.data.data;
      if (data.length > 0) {
        dispatch(
          fetchPolicySef40Success({
            ...data[0],
          })
        );
      } else {
        dispatch(fetchPolicySef40Failed(ERROR_MESSAGES.POLICY_NOT_FOUND));
      }
    } catch (err: any) {
      dispatch(fetchPolicySef40Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const downloadSef40PDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-alberta-sef-forty-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}`,
        {
          responseType: "arraybuffer",
        }
      );

      const pdfBuffer = res.data;

      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "downloaded.pdf";
      link.click();

      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicySef40Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const previewSef40PDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-alberta-sef-forty-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}&is_sef_forty_certificate_preview=true`
      );

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicySef40Failed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// Automobile Insurace

export const FETCH_POLICY_AUTO_INSURANCE_PROGRESS =
  "FETCH_POLICY_AUTO_INSURANCE_PROGRESS";
export const FETCH_POLICY_AUTO_INSURANCE_SUCCESS =
  "FETCH_POLICY_AUTO_INSURANCE_SUCCESS";
export const FETCH_POLICY_AUTO_INSURANCE_FAILED =
  "FETCH_POLICY_AUTO_INSURANCE_FAILED";

export const fetchPolicyAutoInsuranceProgress = () =>
  action(FETCH_POLICY_AUTO_INSURANCE_PROGRESS);

export const fetchPolicyAutoInsuranceSuccess = (data: IAutomobileInsurance) =>
  action(FETCH_POLICY_AUTO_INSURANCE_SUCCESS, {
    data,
  });
export const fetchPolicyAutoInsuranceFailed = (errorMessage: string) =>
  action(FETCH_POLICY_AUTO_INSURANCE_FAILED, { errorMessage });

export const addAutoInsuranceAsync =
  (
    data: IAutomobileInsurance,
    initialData: IAutomobileInsurance,
    isUpdate: boolean,
    policyType: string,
    onCallback: (isSuccess: boolean) => void,
    isDuplicate?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      // const asPayload = {
      //   branch_name: data.branch_name,
      // }

      // const path = await uploadFile(
      //   logo,
      //   "TEST",
      //   data.branch_logo || "",
      //   asPayload
      // );

      if (isDuplicate) {
        await makeApiCall(
          {
            url: "/policy/edit-certificate-of-automobile-insurance",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            incremental_number: null,
            policy_type: policyType,
            // branch_logo: path,
          }
        );
      } else {
        await makeApiCall(
          {
            url: "/policy/edit-certificate-of-automobile-insurance",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            policy_type: policyType,
            // branch_logo: path,
          }
        );
      }

      onCallback(true);
      dispatch(
        showMessage({
          type: "success",
          message: "Certificate of Insurance saved successfully!",
          displayAs: "snackbar",
        })
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchAutoInsuranceAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPolicyAutoInsuranceProgress());
      const res = await api.get(
        `/policy/get-certificate-of-automobile-insurance?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}`
      );
      const data: IAutomobileInsurance[] = res.data.data;
      if (data.length > 0) {
        dispatch(
          fetchPolicyAutoInsuranceSuccess({
            ...data[0],
          })
        );
      } else {
        dispatch(
          fetchPolicyAutoInsuranceFailed(ERROR_MESSAGES.POLICY_NOT_FOUND)
        );
      }
    } catch (err: any) {
      dispatch(fetchPolicyAutoInsuranceFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const downloadAutoInsurancePDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-certificate-of-automobile-insurance-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}`,
        {
          responseType: "arraybuffer",
        }
      );

      const pdfBuffer = res.data;

      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "downloaded.pdf";
      link.click();

      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicyAutoInsuranceFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const previewAutoInsurancePDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-certificate-of-automobile-insurance-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}&is_certificate_of_automobile_insurance_preview=true`
      );

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      onCallback();
    } catch (err: any) {
      dispatch(fetchPolicyAutoInsuranceFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// Cancellation or Reinstatement

export const FETCH_POLICY_CANCELLATION_OR_REINSTATEMENT_PROGRESS =
  "FETCH_POLICY_CANCELLATION_OR_REINSTATEMENT_PROGRESS";
export const FETCH_POLICY_CANCELLATION_OR_REINSTATEMENT_SUCCESS =
  "FETCH_POLICY_CANCELLATION_OR_REINSTATEMENT_SUCCESS";
export const FETCH_POLICY_CANCELLATION_OR_REINSTATEMENT_FAILED =
  "FETCH_POLICY_CANCELLATION_OR_REINSTATEMENT_FAILED";

export const fetchPolicyCancellationReinstatementProgress = () =>
  action(FETCH_POLICY_CANCELLATION_OR_REINSTATEMENT_PROGRESS);

export const fetchPolicyCancellationReinstatementSuccess = (
  data: IAutomobileCancellationReinstatement
) =>
  action(FETCH_POLICY_CANCELLATION_OR_REINSTATEMENT_SUCCESS, {
    data,
  });
export const fetchPolicyCancellationReinstatementFailed = (
  errorMessage: string
) =>
  action(FETCH_POLICY_CANCELLATION_OR_REINSTATEMENT_FAILED, { errorMessage });

export const addCancellationReinstatementAsync =
  (
    data: IAutomobileCancellationReinstatement,
    initialData: IAutomobileCancellationReinstatement,
    isUpdate: boolean,
    policyType: string,
    onCallback: (isSuccess: boolean) => void,
    isDuplicate?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      // const asPayload = {
      //   branch_name: data.branch_name,
      // }

      // const path = await uploadFile(
      //   logo,
      //   "TEST",
      //   data.branch_logo || "",
      //   asPayload
      // );

      if (isDuplicate) {
        await makeApiCall(
          {
            url: "/policy/create-policy-cancellation-or-reinstatement",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            incremental_number: null,
            policy_type: policyType,
            // branch_logo: path,
          }
        );
      } else {
        await makeApiCall(
          {
            url: "/policy/create-policy-cancellation-or-reinstatement",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            policy_type: policyType,
            // branch_logo: path,
          }
        );
      }

      onCallback(true);
      dispatch(
        showMessage({
          type: "success",
          message: "Cancellation Request saved successfully!",
          displayAs: "snackbar",
        })
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchCancellationReinstatementAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    policyType: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPolicyCancellationReinstatementProgress());
      const res = await api.get(
        `/policy/get-policy-cancellation-or-reinstatement?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&policy_type=${policyType}`
      );
      const data: IAutomobileCancellationReinstatement[] = res.data.data;
      if (data.length > 0) {
        dispatch(
          fetchPolicyCancellationReinstatementSuccess({
            ...data[0],
          })
        );
      } else {
        dispatch(
          fetchPolicyCancellationReinstatementFailed(
            ERROR_MESSAGES.POLICY_NOT_FOUND
          )
        );
      }
    } catch (err: any) {
      dispatch(
        fetchPolicyCancellationReinstatementFailed(ERROR_MESSAGES.SERVER_ERROR)
      );
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const downloadCancellationReinstatementPDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-cancellation-and-reinstatement-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}`,
        {
          responseType: "arraybuffer",
        }
      );

      const pdfBuffer = res.data;

      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "downloaded.pdf";
      link.click();

      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(
        fetchPolicyCancellationReinstatementFailed(ERROR_MESSAGES.SERVER_ERROR)
      );
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const previewCancellationReinstatementPDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-cancellation-and-reinstatement-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&is_preview=true`
      );

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      onCallback();
    } catch (err: any) {
      dispatch(
        fetchPolicyCancellationReinstatementFailed(ERROR_MESSAGES.SERVER_ERROR)
      );
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// --- Intact Authorization ---

export const FETCH_POLICY_INTACT_AUTHORIZATION_PROGRESS =
  "FETCH_POLICY_INTACT_AUTHORIZATION_PROGRESS";
export const FETCH_POLICY_INTACT_AUTHORIZATION_SUCCESS =
  "FETCH_POLICY_INTACT_AUTHORIZATION_SUCCESS";
export const FETCH_POLICY_INTACT_AUTHORIZATION_FAILED =
  "FETCH_POLICY_INTACT_AUTHORIZATION_FAILED";

export const fetchPolicyIntactAuthorizationProgress = () =>
  action(FETCH_POLICY_INTACT_AUTHORIZATION_PROGRESS);

export const fetchPolicyIntactAuthorizationSuccess = (
  data: IIntactAuthorization
) => action(FETCH_POLICY_INTACT_AUTHORIZATION_SUCCESS, { data });

export const fetchPolicyIntactAuthorizationFailed = (errorMessage: string) =>
  action(FETCH_POLICY_INTACT_AUTHORIZATION_FAILED, { errorMessage });

export const upsertPolicyIntactAuthorizationAsync =
  (
    data: IIntactAuthorization,
    initialData: IIntactAuthorization,
    isUpdate: boolean,
    onCallback: (isSuccess: boolean) => void,
    isDuplicate?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());

      if (isDuplicate) {
        await makeApiCall(
          {
            url: "/policy/edit-intact-authorization-form",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            incremental_number: null,
          }
        );
      } else {
        await makeApiCall(
          {
            url: "/policy/edit-intact-authorization-form",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
          }
        );
      }

      dispatch(
        showMessage({
          type: "success",
          message: " Payment Authorization-Intact saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Server error",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchPolicyIntactAuthorizationAsync =
  (
    customerPolicyId: number,
    incremental_number: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchPolicyIntactAuthorizationProgress());
      const res = await api.get(
        `/policy/get-intact-authorization-form?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}`
      );
      const data = res.data.data;
      if (data && data.length > 0) {
        dispatch(fetchPolicyIntactAuthorizationSuccess({ ...data[0] }));
      } else {
        dispatch(
          fetchPolicyIntactAuthorizationFailed(ERROR_MESSAGES.POLICY_NOT_FOUND)
        );
      }
    } catch (err: any) {
      dispatch(
        fetchPolicyIntactAuthorizationFailed(ERROR_MESSAGES.SERVER_ERROR)
      );
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Server error",
          displayAs: "snackbar",
        })
      );
    }
  };

export const downloadPolicyIntactAuthorizationPDFAsync =
  (
    customer_policy_id: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/policy/get-intact-authorization-certificate-pdf?customer_policy_id=${customer_policy_id}&incremental_number=${incremental_number}&policy_type=${policyType}`,
        { responseType: "arraybuffer" }
      );
      const pdfBuffer = res.data;
      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "downloaded.pdf";
      link.click();
      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Server error",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const previewPolicyIntactAuthorizationPDFAsync =
  (
    customer_policy_id: number,
    incremental_number: number,
    policyType: string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/policy/get-intact-authorization-certificate-pdf?customer_policy_id=${customer_policy_id}&incremental_number=${incremental_number}&policy_type=${policyType}&is_intact_authorization_certificate_preview=true&&`
      );
      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      onCallback();
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Server error",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// --- End Intact Authorization ---

// ----------Policy Finance ---------
export const FETCH_POLICY_PREMIUM_FINANCE_LIST_PROGRESS =
  "FETCH_POLICY_PREMIUM_FINANCE_LIST_PROGRESS";
export const FETCH_POLICY_PREMIUM_FINANCE_LIST_SUCCESS =
  "FETCH_POLICY_PREMIUM_FINANCE_LIST_SUCCESS";
export const FETCH_POLICY_PREMIUM_FINANCE_LIST_FAILED =
  "FETCH_POLICY_PREMIUM_FINANCE_LIST_FAILED";

export const fetchPolicyPremiumFinanceListProgress = () =>
  action(FETCH_POLICY_PREMIUM_FINANCE_LIST_PROGRESS);
export const fetchPolicyPremiumFinanceListSuccess = (
  data: IPremiumFinance[],
  totalRecords: number
) => action(FETCH_POLICY_PREMIUM_FINANCE_LIST_SUCCESS, { data, totalRecords });
export const fetchPolicyPremiumFinanceListFailed = () =>
  action(FETCH_POLICY_PREMIUM_FINANCE_LIST_FAILED);

export const fetchPolicyPremiumFinanceListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    date: IDataTableV2DateState["dates"],
    customerPolicyId?: number,
    status?: string,
    limit?: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPolicyPremiumFinanceListProgress());
      const pageLimit = limit || rowsInPerPage;
      let finalUrl = `/policy/get-policy-premium-finance-agreement?pageNo=${pageNumber}&pageLimit=${pageLimit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;

      if (customerPolicyId) {
        finalUrl = finalUrl + `&customer_policy_id=${customerPolicyId}`;
      }

      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl);
      const data: IPremiumFinance[] = res.data.data;
      const totalRecotds = res.data.totalRecords;
      dispatch(fetchPolicyPremiumFinanceListSuccess(data, totalRecotds));
    } catch (err: any) {
      dispatch(fetchPolicyPremiumFinanceListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_POLICY_PREMIUM_FINANCE_PROGRESS =
  "FETCH_POLICY_PREMIUM_FINANCE_PROGRESS";
export const FETCH_POLICY_PREMIUM_FINANCE_SUCCESS =
  "FETCH_POLICY_PREMIUM_FINANCE_SUCCESS";
export const FETCH_POLICY_PREMIUM_FINANCE_FAILED =
  "FETCH_POLICY_PREMIUM_FINANCE_FAILED";

export const fetchPolicyPremiumFinanceProgress = () =>
  action(FETCH_POLICY_PREMIUM_FINANCE_PROGRESS);
export const fetchPolicyPremiumFinancerSuccess = (data: IPremiumFinance) =>
  action(FETCH_POLICY_PREMIUM_FINANCE_SUCCESS, { data });
export const fetchPolicyPremiumFinanceFailed = (errorMessage: string) =>
  action(FETCH_POLICY_PREMIUM_FINANCE_FAILED, { errorMessage });

export const fetchPolicyPremiumFinanceAsync =
  (
    customerPolicyId: number,
    incrementNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPolicyPremiumFinanceProgress());
      const res = await api.get(
        `/policy/get-policy-premium-finance-agreement?customer_policy_id=${customerPolicyId}&column=incremental_number&value=${incrementNumber}`
      );
      const data: IPremiumFinance[] = res.data.data;
      if (data.length > 0) {
        const attachments: IFileUpload[] = [];
        for (const item of (data[0].pfa_attachment || []) as any) {
          attachments.push({
            file: null,
            key: getUniqueId(),
            path: item,
          });
        }

        dispatch(
          fetchPolicyPremiumFinancerSuccess({
            ...data[0],
            pfa_attachment: attachments,
          })
        );
      } else {
        dispatch(
          fetchPolicyPremiumFinanceFailed(
            "Oops! We couldn't find any records associated with your fine art number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchPolicyPremiumFinanceFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const addPolicyPremiumFinanceAsync =
  (
    data: IPremiumFinance,
    file: any | null,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        customer_id: data.customer_id,
        policy_type: "BUSINESS",
        customer_policy_id: data.customer_policy_id,
        page_name: "Premium Finance Agreement",
      };

      const { paths } = await uploadMultipleFile(
        data.pfa_attachment || [],
        "POLICY",
        asPayload
      );
      await api.post(`/policy/edit-policy-premium-finance-agreement`, {
        ...data,
        pfa_attachment: paths,
      });
      dispatch(
        showMessage({
          type: "success",
          message: "PFA saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// ----------------------------------

export const FETCH_POLICY_UPDATES_LIST_PROGRESS =
  "FETCH_POLICY_UPDATES_LIST_PROGRESS";
export const FETCH_POLICY_UPDATES_LIST_SUCCESS =
  "FETCH_POLICY_UPDATES_LIST_SUCCESS";
export const FETCH_POLICY_UPDATES_LIST_FAILED =
  "FETCH_POLICY_UPDATES_LIST_FAILED";

export const fetchPolicyUpdatesListProgress = () =>
  action(FETCH_POLICY_UPDATES_LIST_PROGRESS);
export const fetchPolicyUpdatesListSuccess = (
  list: IPolicyUpdates[],
  totalRecords: number
) => action(FETCH_POLICY_UPDATES_LIST_SUCCESS, { list, totalRecords });
export const fetchPolicyUpdatesListFailed = () =>
  action(FETCH_POLICY_UPDATES_LIST_FAILED);

export const fetchPolicyUpdatesListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    date: IDataTableV2DateState["dates"],
    customer_policy_id: number,
    status: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPolicyUpdatesListProgress());
      let finalUrl = `/policy/get-policy-update?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&customer_policy_id=${customer_policy_id}`;
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }

      const res = await api.get(finalUrl);
      const data: IPolicyUpdates[] = res.data.data;

      dispatch(fetchPolicyUpdatesListSuccess(data, res.data.totalRecords));
    } catch (err: any) {
      dispatch(fetchPolicyUpdatesListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertPolicyUpdatesAsync =
  (
    data: IPolicyUpdates,
    initialData: IPolicyUpdates,
    isUpdate: boolean,
    file: File | null,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        customer_id: data.customer_id,
        customer_policy_id: data.customer_policy_id,
        policy_type: "POLICY",
        page_name: "UPDATES",
      };
      const path = await uploadFile(
        file,
        "POLICY",
        data.attachment || "",
        asPayload
      );

      await makeApiCall(
        {
          url: endPoints.customerPolicyUpdate.upsert,
          method: "POST",
          automation: {
            primaryFieldName: "policy_update_id",
            isUpdate: isUpdate,
          },
        },
        initialData,
        { ...data, attachment: path }
      );

      dispatch(
        showMessage({
          type: "success",
          message: "Policy Commission saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertPolicyUpdatesFromPublicFormAsync =
  (
    data: IPolicyUpdates,
    file: File | null,
    key: string,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        customer_id: data.customer_id,
        customer_policy_id: data.customer_policy_id,
        policy_type: "POLICY",
        page_name: "UPDATES",
      };
      const path = await uploadFile(
        file,
        "POLICY",
        data.attachment || "",
        asPayload
      );

      await makeApiCall(
        {
          url: endPoints.customerPolicyUpdate.publicUpsert,
          method: "POST",
          automation: {
            primaryFieldName: "policy_update_id",
            isUpdate: true,
          },
          headers: {
            "Auth-Key": key,
          },
        },
        {},
        { ...data, attachment: path },
        true
      );

      dispatch(
        showMessage({
          type: "success",
          message: "Data saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const getPolicyUpdateShareCode =
  (
    policy_update_id: string,
    onCallback: (isSuccess: boolean, key?: string) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());

      const res = await api.post("/policy/generate-external-auth", {
        policy_update_id: policy_update_id,
      });

      onCallback(true, res.data.data.auth_key);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

//-----------------------------

//---------------------------------
export const FETCH_POLICY_LIABILITY_SLIP_PROGRESS =
  "FETCH_POLICY_LIABILITY_SLIP_PROGRESS";
export const FETCH_POLICY_LIABILITY_SLIP_SUCCESS =
  "FETCH_POLICY_LIABILITY_SLIP_SUCCESS";
export const FETCH_POLICY_LIABILITY_SLIP_FAILED =
  "FETCH_POLICY_LIABILITY_SLIP_FAILED";

export const fetchPolicyLiabilitySlipProgress = () =>
  action(FETCH_POLICY_LIABILITY_SLIP_PROGRESS);
export const fetchPolicyLiabilitySlipSuccess = (data: IPolicyLiabilitySlips) =>
  action(FETCH_POLICY_LIABILITY_SLIP_SUCCESS, { data });
export const fetchPolicyLiabilitySlipFailed = (errorMessage: string) =>
  action(FETCH_POLICY_LIABILITY_SLIP_FAILED, { errorMessage });

export const fetchPolicyLiabilityAsync =
  (
    customerPolicyId: number,
    incremental_number: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPolicyLiabilitySlipProgress());
      const res = await api.get(
        `/policy/get-policy-liability-slip-certificate?customer_policy_id=${customerPolicyId}&column=incremental_number&value=${incremental_number}`
      );
      const data: IPolicyLiabilitySlips[] = res.data.data;
      if (data.length > 0) {
        dispatch(
          fetchPolicyLiabilitySlipSuccess({
            ...data[0],
          })
        );
      } else {
        dispatch(
          fetchPolicyLiabilitySlipFailed(ERROR_MESSAGES.POLICY_NOT_FOUND)
        );
      }
    } catch (err: any) {
      dispatch(fetchPolicyLiabilitySlipFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertLiabilitySlipAsync =
  (
    data: IPolicyLiabilitySlips,
    initialData: IPolicyLiabilitySlips,
    isUpdate: boolean,
    onCallback: (isSuccess: boolean) => void,
    isDuplicate?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      if (isDuplicate) {
        await makeApiCall(
          {
            url: "/policy/edit-policy-liability-slip-certificate",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            incremental_number: null,
          }
        );
      } else {
        await makeApiCall(
          {
            url: "/policy/edit-policy-liability-slip-certificate",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
          }
        );
      }

      onCallback(true);
      dispatch(
        showMessage({
          type: "success",
          message: "Liability slip saved successfully!",
          displayAs: "snackbar",
        })
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const downloadLiabilitySlipsPDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/policy/get-policy-liability-slip-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}`,
        {
          responseType: "arraybuffer",
        }
      );

      const pdfBuffer = res.data;

      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "downloaded.pdf";
      link.click();

      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const previewLiabilitySlipsPDFAsync =
  (
    customerPolicyId: number,
    incremental_number: number,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-policy-liability-slip-certificate-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incremental_number}&is_liability_slip_certificate_preview=true`
      );

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      onCallback();
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

//---------------------------------

export const CLEAR_POLICY_COMMON_STATE = "CLEAR_POLICY_COMMON_STATE";
export const clearPolicyCommonState = () => action(CLEAR_POLICY_COMMON_STATE);
